import DeviceManager from '../utils/device-manager';

export function initHeader() : void {
    const $header = document.querySelector<HTMLElement>('header.header');

    if ($header) {
        new Header($header);
    }
}

class Header {
    $container: HTMLElement;
    canBeSticky = false;
    headerSearchHeight = 0;
    smallDeviceStickyInitialized = false;
    largeDeviceStickyInitialized = false;
    smallDeviceNavInitialized = false;
    lastScrollTop = 0; // The last scrollY
    $menuToggler: HTMLElement|null = null; // The small device menu toggler
    $searchToggler: HTMLButtonElement|null = null; // The small device search panel toggler
    $menuItemWithChildren: NodeListOf<HTMLElement>|null = null; // The small device menu item with children
    handleOnScrollStickyLargeDeviceHandler = this.onScrollStickyLargeDeviceHandler.bind(this);
    handleOnScrollStickySmallDeviceHandler = this.onScrollStickySmallDeviceHandler.bind(this);
    handleToggleMenuOnSmallDeviceHandler = this.toggleMenuOnSmallDeviceHandler.bind(this);
    handleToggleSearchOnSmallDeviceHandler = this.toggleSearchOnSmallDeviceHandler.bind(this);

    constructor($container: HTMLElement) {
        this.$container = $container;

        this.initSticky();

        DeviceManager.addCallback((attr) => {
            if (this.smallDeviceNavInitialized && DeviceManager.isGreaterThanOrEqual(attr.current, 'xl')) { // Large device
                this.destroySmallDeviceNav();
            } else if (!this.smallDeviceNavInitialized && !DeviceManager.isGreaterThanOrEqual(attr.current, 'xl')) { // Small device
                this.initSmallDeviceNav();
            }
        });
    }

    /**
     * Initialize the sticky header logic
     * 
     * @return {void}
     */
    initSticky() : void {
        DeviceManager.addCallback(attr => {
            this.checkIfCanBeSticky();

            if (DeviceManager.isGreaterThanOrEqual(attr.current, 'xl')) { // Large device
                if (this.smallDeviceStickyInitialized) {
                    this.destroyStickyOnSmallDevice();
                }

                if (this.canBeSticky && !this.largeDeviceStickyInitialized) {
                    this.initStickyOnLargeDevice();
                }
            } else if (!DeviceManager.isGreaterThanOrEqual(attr.current, 'xl')) { // Small device
                if (this.largeDeviceStickyInitialized) {
                    this.destroyStickyOnLargeDevice();
                }

                if (this.canBeSticky && !this.smallDeviceStickyInitialized) {
                    this.initStickyOnSmallDevice();
                }
            }
        });
    }

    /**
     * Initialize the sticky header on large device
     * 
     * @return {void}
     */
    initStickyOnLargeDevice() : void {
        this.largeDeviceStickyInitialized = true;

        this.headerSearchHeight = this.$container.querySelector('.header-search')?.clientHeight ?? 0;

        this.$container.classList.add('is-sticky');

        this.onScrollStickyLargeDeviceHandler();
        window.addEventListener('scroll', this.handleOnScrollStickyLargeDeviceHandler);
    }

    /**
     * Initialize the sticky header on small device
     * 
     * @return {void}
     */
    initStickyOnSmallDevice() : void {
        this.smallDeviceStickyInitialized = true;
        this.lastScrollTop = 0;
        this.$container.classList.add('is-sticky');

        this.onScrollStickySmallDeviceHandler();
        window.addEventListener('scroll', this.handleOnScrollStickySmallDeviceHandler);
    }

    /**
     * Handler for scroll event when sticky is enabled on large device
     * 
     * @return {void}
     */
    onScrollStickyLargeDeviceHandler() : void {
        this.applyStickyClasses(this.headerSearchHeight);
    }

    /**
     * Handler for scroll event when sticky is enabled on small device
     * 
     * @return {void}
     */
    onScrollStickySmallDeviceHandler() : void {
        this.applyStickyClasses();

        const scrollDown = window.scrollY > this.lastScrollTop;

        if (scrollDown && window.scrollY > 20) { // Hide if scroll down
            this.$container.style.transform = `translateY(-${this.$container.clientHeight}px)`;
        } else { // Show if scroll up
            this.$container.style.transform = '';
        }

        this.lastScrollTop = window.scrollY <= 0 ? 0 : window.scrollY;
    }

    /**
     * Destroy the sticky header on large device
     * 
     * @return {void}
     */
    destroyStickyOnLargeDevice() : void {
        this.largeDeviceStickyInitialized = false;

        this.$container.classList.remove('is-sticky');
        this.$container.classList.remove('is-sticky--active');
        window.removeEventListener('scroll', this.handleOnScrollStickyLargeDeviceHandler);
    }

    /**
     * Destroy the sticky header on small device
     * 
     * @return {void}
     */
    destroyStickyOnSmallDevice() : void {
        this.smallDeviceStickyInitialized = false;

        this.$container.classList.remove('is-sticky');
        this.$container.classList.remove('is-sticky--active');
        this.lastScrollTop = 0;
        this.$container.style.transform = '';
        window.removeEventListener('scroll', this.handleOnScrollStickySmallDeviceHandler);
    }

    /**
     * Apply sticky classes according to current scroll position
     * 
     * @param {number} offset
     * 
     * @return {void}
     */
    applyStickyClasses(offset = 0) : void {
        if (window.scrollY > offset) {
            document.body.classList.add('sticky-header');
            this.$container.classList.add('is-sticky--active');
        } else {
            document.body.classList.remove('sticky-header');
            this.$container.classList.remove('is-sticky--active');
        }
    }

    /**
     * Detect if the header can be sticky and set the property "canBeSticky"
     * 
     * @return {void}
     */
    checkIfCanBeSticky() : void {
        const $stickyBar = document.querySelector('.sticky-bar');
        const anotherStickyIsDisplayed = $stickyBar && 'none' !== window.getComputedStyle($stickyBar).display;

        this.canBeSticky = !anotherStickyIsDisplayed;
    }

    /**
     * Initialize the small device navigation logic
     * 
     * @return {void}
     */
    initSmallDeviceNav() : void {
        this.smallDeviceNavInitialized = true;

        this.$menuToggler = this.$container.querySelector<HTMLElement>('.header-burger');
        this.$searchToggler = this.$container.querySelector<HTMLButtonElement>('.js-header-mobile-btn-search');
        this.$menuItemWithChildren = this.$container.querySelectorAll<HTMLElement>('.js-level1-item-menu-with-subitem');

        if (this.$menuToggler) {
            this.$menuToggler.addEventListener('click', this.handleToggleMenuOnSmallDeviceHandler);
        }
        
        if (this.$searchToggler) {
            this.$searchToggler.addEventListener('click', this.handleToggleSearchOnSmallDeviceHandler);
        }

        this.$menuItemWithChildren.forEach($menuItem => {
            const $btnOpen = $menuItem.querySelector('a[href="#"]') as HTMLButtonElement;
            const $btnBack = this.createSubMenuBackButton($btnOpen);

            $menuItem.querySelector('.sub-menu')?.prepend($btnBack);
            
            $btnOpen.addEventListener('click', (e: MouseEvent) => {
                e.preventDefault();
                this.openSubMenuOnSmallDevice($menuItem);
            });

            $btnBack.addEventListener('click', (e: MouseEvent) => {
                e.preventDefault();
                this.closeSubMenuOnSmallDevice($menuItem);
            });
        });
    }

    /**
     * Handler to toggle menu on small device
     * 
     * @return {void}
     */
    toggleMenuOnSmallDeviceHandler() : void {
        if (document.body.classList.contains('menu-is-open')) {
            this.closeMenuOnSmallDevice();
        } else {
            this.closeSearchOnSmallDevice();
            this.openMenuOnSmallDevice();
        }
    }

    /**
     * Handler to toggle seach panel on small device
     * 
     * @return {void}
     */
    toggleSearchOnSmallDeviceHandler() : void {
        if (document.body.classList.contains('search-is-open')) {
            this.closeSearchOnSmallDevice();
        } else {
            this.closeMenuOnSmallDevice();
            this.openSearchOnSmallDevice();
        }
    }

    /**
     * Open the menu on small device
     * 
     * @return {void}
     */
    openMenuOnSmallDevice() : void {
        document.body.classList.add('menu-is-open', 'no-scroll');
    }

    /**
     * Close the menu on small device
     * 
     * @return {void}
     */
    closeMenuOnSmallDevice() : void {
        document.body.classList.remove('menu-is-open', 'no-scroll');
    }

    /**
     * Open the search panel on small device
     * 
     * @return {void}
     */
    openSearchOnSmallDevice() : void {
        document.body.classList.add('search-is-open', 'no-scroll');
    }

    /**
     * Close the search panel on small device
     * 
     * @return {void}
     */
    closeSearchOnSmallDevice() : void {
        document.body.classList.remove('search-is-open', 'no-scroll');
    }

    /**
     * Open submenu on small device
     * 
     * @param {HTMLElement} $element
     * 
     * @return {void}
     */
    openSubMenuOnSmallDevice($element: HTMLElement) : void {
        $element.classList.add('open-submenu');
    }

    /**
     * Close submenu on small device
     * 
     * @param {HTMLElement} $element
     * 
     * @return {void}
     */
    closeSubMenuOnSmallDevice($element: HTMLElement) : void {
        $element.classList.remove('open-submenu');
    }

    /**
     * Create sub Menu back button on small device
     * 
     * @param {HTMLButtonElement} $element
     * 
     * @return {HTMLButtonElement}
     */
    createSubMenuBackButton($element: HTMLButtonElement) : HTMLButtonElement {
        const $btnBack = $element.cloneNode(true) as HTMLButtonElement;
        $btnBack.classList.add('sub-menu-back');

        return $btnBack;
    }

    /**
     * Destroy sub Menu back button on small device
     * 
     * @return {void}
     */
    destroySubMenuBackButton() : void {
        const $btnBack = document.querySelectorAll('.sub-menu-back');
        $btnBack.forEach($element => {
            $element.remove();
        });
    }

    /**
     * Destroy the small device navigation logic
     * 
     * @return {void}
     */
    destroySmallDeviceNav() : void {
        this.smallDeviceNavInitialized = false;

        this.$menuToggler?.removeEventListener('click', this.handleToggleMenuOnSmallDeviceHandler);
        this.destroySubMenuBackButton();
        this.closeMenuOnSmallDevice();
    }
}
